import { type FC, type RefObject, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import * as Sentry from '@sentry/react'
import { Button } from '../Button/Button'
import { HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../common/copy'

type CopyState = 'idle' | 'copied' | 'failed'

const LABELS: Record<CopyState, string> = {
  idle: 'Copy',
  copied: 'Copied',
  failed: 'Copy failed',
}

const ICONS: Record<CopyState, string> = {
  idle: 'bi-clipboard',
  copied: 'bi-check2',
  failed: 'bi-exclamation-circle',
}

const RESET_DELAY_MS = 2000

const escapeHtml = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

interface CopyOutputButtonProps {
  text: string
  targetRef?: RefObject<HTMLElement>
}

export const CopyOutputButton: FC<CopyOutputButtonProps> = ({ text, targetRef }) => {
  const [state, setState] = useState<CopyState>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current)
      }
    },
    [],
  )

  const onCopy = async () => {
    const html = targetRef?.current?.innerHTML
    try {
      await writeCopyContentToClipboard({
        [TEXT_TYPE]: text,
        [HTML_TYPE]: html && html.length > 0 ? html : escapeHtml(text),
      })
      setState('copied')
    } catch (error) {
      Sentry.captureException(error)
      setState('failed')
    }
    if (timer.current !== null) {
      clearTimeout(timer.current)
    }
    timer.current = setTimeout(() => setState('idle'), RESET_DELAY_MS)
  }

  return (
    <Button type='button' variant='secondaryGhostButton' className='px-2 py-1 rounded-2' onClick={() => void onCopy()} aria-live='polite'>
      <span className='paragraph-extra-small medium d-inline-flex align-items-center gap-1'>
        <i className={classNames('bi', ICONS[state])} aria-hidden='true' />
        {LABELS[state]}
      </span>
    </Button>
  )
}
